import React, { useEffect, useState } from 'react'

import TemplateDto from '../interfaces/TemplateDto'
import Client from '../interfaces/Client'
import { useAuth } from '../hooks/useAuth'
import useTemplateFetcher from '../hooks/useTemplateFetcher'
import { AppPath } from '../enums/AppPath'
import VehicleBase from '../interfaces/VehicleBase'
import { formatWithThousandSep } from '../utils/utils'
import { DEFAULT_TITLE_CONFIG, TitleConfig } from '../utils/documentTitleUtils'
import { useFeatureFlag } from '../hooks/useFeatureFlag'

import { ButtonAction, ButtonCheckConfirma } from './Buttons'
import DocFooter from './DocFooter'
import ConfirmationModal from './ConfirmationModal'
import { SalenoteFormat } from './SalenoteEdit'
import SalenoteDetail from './SalenoteDetail'
import { PrintableCard } from './PrintableCard'

export interface SalenoteDet extends SalenoteFormat {
  titleConfig?: TitleConfig
  body: {
    vehicle: VehicleBase | null
    organization: {
      pictures: { logotype: string; isotype: string }
      nin: string
      legalName: string
      name: string
      business: string
      phone: string
      email: string
      domain: string
      address: string
    }
    client: {
      nin: string
      name: string
      phone: string
      email: string
      address: string
    }
  }
}

const SalenotePreview: React.FC<{
  vehicle: VehicleBase
  client: Client
  salenote: SalenoteFormat
  creandoSw: boolean
  modificandoSw: boolean
  errors: { [key: string]: string }
  onGoBack: () => void
  onCrearClick: () => void
  onCancelModify: () => void
  esAprobada?: boolean
  esAnulada?: boolean
  loading?: boolean
}> = ({
  vehicle,
  client,
  salenote,
  creandoSw,
  modificandoSw,
  errors,
  onGoBack,
  onCrearClick,
  onCancelModify,
  esAprobada,
  esAnulada,
  loading,
}) => {
  const { useNewDocumentUI } = useFeatureFlag()
  const { userData } = useAuth()
  const { errorTemplate, fetchTemplate } = useTemplateFetcher()
  const [template, setTemplate] = useState<TemplateDto | null>(null)
  const organizationId = userData?.organization?._id
  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    const fetchDataAsync = async () => {
      const docType = AppPath.SaleNotes
      const data = organizationId ? await fetchTemplate(organizationId, docType) : null
      setTemplate(data)
    }
    fetchDataAsync()
  }, [organizationId])

  const onCancelModClick = () => {
    setIsModalOpen(true)
  }

  const handleConfirm = () => {
    setIsModalOpen(false)
    // Perform delete action here
    onCancelModify()
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  // Rellenamos con los datos devueltos desde el template, el cliente y
  // el vehiculo
  salenote.footer = template?.salenotes.footer.value

  // Extract title configuration from template
  // For preview/edit mode, always use the template configuration
  const titleConfig = template?.salenotes?.title ?? { ...DEFAULT_TITLE_CONFIG }

  const org = template?.general.organization
  const salenoteDet: SalenoteDet = {
    ...salenote,
    titleConfig,
    body: {
      vehicle: vehicle,
      organization: {
        pictures: org?.pictures ?? { logotype: '', isotype: '' },
        nin: org?.nin?.value ?? '',
        legalName: '',
        name: org?.name.value ?? '',
        business: org?.business.type !== '-' ? (org?.business.value ?? '') : '',
        phone: org?.phone.value ?? '',
        email: org?.email?.value ?? '',
        domain: org?.domain?.value ?? '',
        address: org?.address?.value ?? '',
      },

      client: {
        nin: client?.nin ?? '',
        name: `${client?.firstname} ${client?.lastname}`,
        phone: client?.phone ?? '',
        email: client?.email ?? '',
        address: client?.address ?? '',
      },
    },
  }
  const names = {
    profileName: salenoteDet.profile_name ?? '',
    organizationName: salenoteDet.body.organization.name,
    clientName: `${salenoteDet.body.client.name}`,
  }
  const space = '\u00A0'
  const doc = 'Nota de venta'
  const buttonLabel = `${space}${space}Volver a la ${doc}${space}${space}`
  const buttonCreaLabel = creandoSw ? 'Crear' : 'Grabar cambios'

  return (
    <>
      <div className={`mx-auto ${useNewDocumentUI ? 'p-4 print:p-0 max-w-6xl print:max-w-full' : 'p-2 max-w-3xl'}`}>
        {/* Purchase Links Section - OUTSIDE document */}
        {!creandoSw && !modificandoSw && esAprobada && salenoteDet.vehicles && salenoteDet.vehicles.length > 0 && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <div className="text-sm font-medium text-green-900">Vehículos en Parte de Pago</div>
                <div className="text-sm text-green-700 space-y-1">
                  {salenoteDet.vehicles.map((vehicle, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span>
                        {vehicle.description} - ${formatWithThousandSep(vehicle.price)}
                      </span>
                      {vehicle.purchaseCreated && vehicle.associatedPurchaseId ? (
                        <a
                          href={`/purchases/view/${vehicle.associatedPurchaseId}`}
                          className="ml-2 text-blue-600 hover:text-blue-800 underline text-xs"
                        >
                          Ver Compra #{vehicle.purchaseNumber || 'Auto-generada'} →
                        </a>
                      ) : (
                        <span className="ml-2 text-amber-600 text-xs">Compra pendiente</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <PrintableCard>
          <SalenoteDetail
            salenote={salenoteDet}
            errors={errors}
            esAprobada={esAprobada}
            esAnulada={esAnulada}
            showPurchaseLinks={false} // NO purchase links inside document
          />
          <DocFooter names={names} />
        </PrintableCard>

        {(creandoSw || modificandoSw) && (
          <ButtonCheckConfirma
            title={'Confirma'}
            subTitle={''}
            checkLabel={`Confirmo que los datos de la ${doc} son correctos.`}
            buttonLabel={loading ? 'Procesando...' : buttonCreaLabel}
            handleCrearClick={onCrearClick}
            disabled={loading}
          />
        )}
        <div className="flex justify-center mt-1">
          {(creandoSw || modificandoSw) && (
            <div className="mr-4">
              <ButtonAction label={buttonLabel} handleButtonClick={onGoBack} />
            </div>
          )}
          {modificandoSw && <ButtonAction label={`Cancelar modificaciones`} handleButtonClick={onCancelModClick} />}
        </div>
      </div>
      {errorTemplate && <div>Error: {errorTemplate.message}</div>}

      {isModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro? Perderás los cambios hechos."
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  )
}

export default SalenotePreview
